import { getSafeNotificationRoute } from "./notificationRoutes";

export const NOTIFICATION_CATEGORIES = Object.freeze([
  "account_security",
  "messages_calls",
  "journeys_proposals",
  "employment",
  "services",
  "appointments_agenda",
  "marketplace_transactions",
  "saved_searches_alerts",
  "community_events",
  "wallet_rewards",
  "protection_moderation",
  "projects_crowdfunding",
  "system",
]);

export const NOTIFICATION_UNIVERSES = Object.freeze([
  "acheter",
  "vendre",
  "emploi",
  "services",
  "voyage",
  "immobilier",
  "animaux",
  "babysitting",
  "crowdfunding",
  "forum",
  "groupes",
  "agenda",
  "wallet",
  "security",
]);

export const CATEGORY_LABELS = Object.freeze({
  account_security: "Compte et sécurité",
  messages_calls: "Messages et appels",
  journeys_proposals: "Démarches et propositions",
  employment: "Emploi",
  services: "Services",
  appointments_agenda: "Rendez-vous et agenda",
  marketplace_transactions: "Marketplace et transactions",
  saved_searches_alerts: "Recherches et alertes",
  community_events: "Communauté et événements",
  wallet_rewards: "Portefeuille et récompenses",
  protection_moderation: "Protection et modération",
  projects_crowdfunding: "Projets et crowdfunding",
  system: "Eternua",
});

export const UNIVERSE_LABELS = Object.freeze({
  acheter: "Acheter",
  vendre: "Vendre",
  emploi: "Emploi",
  services: "Services",
  voyage: "Voyage",
  immobilier: "Immobilier",
  animaux: "Animaux",
  babysitting: "Babysitting",
  crowdfunding: "Crowdfunding",
  forum: "Forum",
  groupes: "Groupes",
  agenda: "Agenda",
  wallet: "Portefeuille",
  security: "Sécurité",
});

export const NOTIFICATION_STATUSES = Object.freeze([
  "all",
  "unread",
  "read",
  "unseen",
  "seen",
  "archived",
  "important",
]);

export const NOTIFICATION_ACTIONS = Object.freeze([
  "mark_seen",
  "mark_read",
  "mark_unread",
  "mark_important",
  "unmark_important",
  "archive",
  "unarchive",
]);

export const NOTIFICATION_BULK_ACTIONS = Object.freeze([
  ...NOTIFICATION_ACTIONS,
  "mark_all_read",
]);

export const DEFAULT_NOTIFICATION_FILTERS = Object.freeze({
  status: "all",
  category: "",
  universe: "",
});

function nullableText(value) {
  if (value === null || value === undefined || value === "") return null;
  return String(value);
}

function requiredId(value) {
  const id = String(value || "").trim();
  if (!id) throw new Error("Notification serveur invalide : identifiant absent.");
  return id;
}

export function normalizeNotification(item) {
  if (!item || typeof item !== "object" || Array.isArray(item)) {
    throw new Error("Notification serveur invalide.");
  }
  const id = requiredId(item.id ?? item.public_id);
  const seenAt = nullableText(item.seen_at ?? item.seenAt);
  const readAt = nullableText(item.read_at ?? item.readAt);
  const archivedAt = nullableText(item.archived_at ?? item.archivedAt);

  return {
    id,
    publicId: id,
    eventType: String(item.event_type ?? item.eventType ?? "system.unknown"),
    category: String(item.category || "system"),
    sourceUniverse: String(item.source_universe ?? item.sourceUniverse ?? ""),
    sourceType: String(item.source_type ?? item.sourceType ?? ""),
    sourceId: String(item.source_id ?? item.sourceId ?? ""),
    sourceVersion: String(item.source_version ?? item.sourceVersion ?? ""),
    title: String(item.title || "Notification Eternua"),
    message: String(item.message || ""),
    link: getSafeNotificationRoute(item.link, ""),
    important: item.important === true,
    ...(typeof (item.action_required ?? item.actionRequired) === "boolean"
      ? { actionRequired: item.action_required ?? item.actionRequired } : {}),
    seenAt,
    readAt,
    archivedAt,
    createdAt: nullableText(item.created_at ?? item.createdAt),
    updatedAt: nullableText(item.updated_at ?? item.updatedAt),
    isSeen: item.is_seen === true || item.isSeen === true || Boolean(seenAt),
    isRead: item.is_read === true || item.isRead === true || Boolean(readAt),
    isArchived:
      item.is_archived === true || item.isArchived === true || Boolean(archivedAt),
    available: item.available !== false,
  };
}

export function normalizeNotificationFilters(filters = {}) {
  const status = NOTIFICATION_STATUSES.includes(filters?.status)
    ? filters.status
    : DEFAULT_NOTIFICATION_FILTERS.status;
  const category = String(filters?.category || "").trim();
  const universe = String(filters?.universe || filters?.sourceUniverse || "").trim();
  return {
    status,
    category: NOTIFICATION_CATEGORIES.includes(category) ? category : "",
    universe: NOTIFICATION_UNIVERSES.includes(universe) ? universe : "",
  };
}

export function cursorFromPageLink(value) {
  if (typeof value !== "string" || !value.trim()) return null;
  const link = value.trim();
  try {
    const base =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "https://eternua.invalid";
    const parsed = new URL(link, base);
    return parsed.searchParams.get("cursor") || null;
  } catch (_error) {
    return null;
  }
}

export function normalizeNotificationPage(payload) {
  const rows = Array.isArray(payload) ? payload : payload?.results;
  if (!Array.isArray(rows)) {
    throw new Error("Liste de notifications serveur invalide.");
  }
  const next = typeof payload?.next === "string" ? payload.next : null;
  const previous = typeof payload?.previous === "string" ? payload.previous : null;
  return {
    items: rows.map(normalizeNotification),
    next,
    previous,
    nextCursor: cursorFromPageLink(next),
    previousCursor: cursorFromPageLink(previous),
    hasMore: Boolean(next),
  };
}

export function normalizeUnreadCount(payload) {
  const count = Number(payload?.unread_count ?? payload?.unreadCount);
  if (!Number.isSafeInteger(count) || count < 0) {
    throw new Error("Compteur de notifications serveur invalide.");
  }
  return count;
}

export function normalizeNotificationPreference(item) {
  if (!item || typeof item !== "object" || Array.isArray(item)) {
    throw new Error("Préférence de notification serveur invalide.");
  }
  const category = String(item.category || "").trim();
  if (!NOTIFICATION_CATEGORIES.includes(category)) {
    throw new Error("Préférence de notification serveur invalide : catégorie inconnue.");
  }
  return {
    category,
    inAppEnabled: item.in_app_enabled !== false,
    pushEnabled: item.push_enabled !== false,
    emailEnabled: item.email_enabled === true,
    emailFrequency: String(item.email_frequency || "never"),
    mandatory: item.mandatory === true,
  };
}

export function normalizeNotificationPreferences(payload) {
  if (!Array.isArray(payload?.results)) {
    throw new Error("Préférences de notification serveur invalides.");
  }
  return {
    items: payload.results.map(normalizeNotificationPreference),
    emailDigestsEnabled: payload.email_digests_enabled === true,
    availableEmailFrequencies: Array.isArray(payload.available_email_frequencies)
      ? payload.available_email_frequencies.map(String)
      : ["immediate", "never"],
    quietHours: {
      enabled: payload.quiet_hours?.enabled === true,
      start: String(payload.quiet_hours?.start || "22:00"),
      end: String(payload.quiet_hours?.end || "07:00"),
      timezone: String(payload.quiet_hours?.timezone || "Europe/Paris"),
    },
  };
}

export function mergeNotificationItems(currentItems, incomingItems) {
  const incomingById = new Map(incomingItems.map((item) => [item.id, item]));
  const merged = currentItems.map((item) => incomingById.get(item.id) || item);
  const existingIds = new Set(currentItems.map((item) => item.id));
  incomingItems.forEach((item) => {
    if (!existingIds.has(item.id)) merged.push(item);
  });
  return merged;
}
